import type { BitmapLayout } from './layout-cache'

/**
 * One shared WebGL2 context draws every bitmap on the page. Browsers cap live
 * WebGL contexts (around 16), so each tile owns only a cheap 2D canvas and we
 * blit the shared framebuffer into it after each frame.
 *
 * The vertex shader carries the whole entry animation: every square flies in
 * from a pseudo-random point outside the canvas, staggered by its index and
 * weighted by its size, with an ease-out-back overshoot. After the entry the
 * cursor pushes squares away and lights up the small ones.
 */

export const BITMAP_BG = [13 / 255, 17 / 255, 23 / 255] as const
export const BITMAP_TILE_RGB = [203, 120, 37] as const
export const ENTRY_TOTAL_MS = 3000
export const MAX_INSTANCES = 16384

const VERTEX_SHADER = `#version 300 es
precision highp float;

in vec2 a_quadPos;
in vec4 a_instanceData;

uniform float u_canvasSize;
uniform float u_layoutWidth;
uniform float u_usedHeight;
uniform float u_squareCount;
uniform float u_startTime;
uniform float u_currentTime;
uniform vec2  u_mouse;
uniform float u_flickerIndex;
uniform float u_scale;
uniform float u_enableRepulsion;
uniform float u_enableFlicker;

out float v_brightness;
out float v_alpha;
out float v_proximityGlow;

float easeOutBack(float x) {
  float c1 = 1.70158;
  float c3 = c1 + 1.0;
  float t = x - 1.0;
  return 1.0 + c3 * t * t * t + c1 * t * t;
}

void main() {
  float x     = a_instanceData.x;
  float y     = a_instanceData.y;
  float size  = a_instanceData.z;
  float index = a_instanceData.w;

  float draw        = max(u_layoutWidth, u_usedHeight);
  float gridSize    = u_canvasSize / draw;
  float offsetY     = (u_canvasSize - u_usedHeight * gridSize) * 0.5;
  float unitPadding = gridSize * 0.25;

  float baseDuration  = 1400.0;
  float totalStagger  = 1200.0;
  float massFactor    = size * 50.0;
  float staggerDelay  = (index / u_squareCount) * totalStagger + abs(sin(index)) * 100.0;
  float elapsed       = u_currentTime - u_startTime - staggerDelay;
  float duration      = baseDuration + massFactor;

  float overallMs     = u_currentTime - u_startTime;
  float overallProg   = clamp(overallMs / 3000.0, 0.0, 1.0);
  float currentProg   = overallProg >= 1.0 ? 1.0 : clamp(elapsed / duration, 0.0, 1.0);

  if (currentProg <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
    v_alpha = 0.0;
    v_brightness = 1.0;
    v_proximityGlow = 0.0;
    return;
  }

  float eased = easeOutBack(currentProg);

  float tx = x;
  float ty = y;
  float shrinkFactor = 1.0;
  v_proximityGlow = 0.0;

  if (u_enableRepulsion > 0.5 && overallProg >= 1.0 && u_mouse.x >= 0.0) {
    float mx = u_mouse.x / gridSize;
    float my = (u_mouse.y - offsetY) / gridSize;
    float dx = tx + size * 0.5 - mx;
    float dy = ty + size * 0.5 - my;
    float dist = sqrt(dx * dx + dy * dy);
    float radius = 18.0;
    if (dist < radius && dist > 0.001) {
      float sFactor = smoothstep(0.0, radius, dist);
      if (size <= 1.0) {
        v_proximityGlow = 1.0 - sFactor;
      } else {
        float force = (1.0 - dist / radius) * 5.0;
        tx += (dx / dist) * force;
        ty += (dy / dist) * force;
        shrinkFactor = sFactor;
        if (size <= 4.0) {
          v_proximityGlow = 1.0 - shrinkFactor;
          shrinkFactor = max(shrinkFactor, 0.5);
        }
      }
    }
  }

  float angle = sin(index * 1234.56) * 6.283185307;
  float distFromCenter = (u_canvasSize / gridSize) * (1.2 + cos(index * 789.1) * 0.5);
  float startX = x + cos(angle) * distFromCenter;
  float startY = y + sin(angle) * distFromCenter;

  float curX = startX + (tx - startX) * eased;
  float curY = startY + (ty - startY) * eased;

  float px = curX * gridSize + unitPadding;
  float py = curY * gridSize + offsetY + unitPadding;
  float pw = size * gridSize - unitPadding * 2.0;

  px = floor(px);
  py = floor(py);
  pw = ceil(pw);

  float shrunkPw = pw * shrinkFactor;
  float shrinkOffset = (pw - shrunkPw) * 0.5;
  px += shrinkOffset;
  py += shrinkOffset;
  pw = shrunkPw;

  if (pw <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
    v_alpha = 0.0;
    v_brightness = 1.0;
    v_proximityGlow = 0.0;
    return;
  }

  float vx = px + a_quadPos.x * pw;
  float vy = py + a_quadPos.y * pw;

  float cx = u_canvasSize * 0.5;
  float cy = u_canvasSize * 0.5;
  vx = cx + (vx - cx) * u_scale;
  vy = cy + (vy - cy) * u_scale;

  gl_Position = vec4((vx / u_canvasSize) * 2.0 - 1.0, 1.0 - (vy / u_canvasSize) * 2.0, 0.0, 1.0);

  bool isFlicker = u_enableFlicker > 0.5 && abs(index - u_flickerIndex) < 0.5;
  float glowBrightness = 1.0 + v_proximityGlow * 1.2;
  v_brightness = isFlicker ? 1.6 : glowBrightness;
  v_alpha = isFlicker ? 1.0 : min(1.0, currentProg * 1.5);
}
`

const FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform vec3 u_baseColor;

in float v_brightness;
in float v_alpha;
in float v_proximityGlow;

out vec4 fragColor;

void main() {
  vec3 color = min(vec3(1.0), u_baseColor * v_brightness);
  fragColor = vec4(color, v_alpha);
}
`

const ISO_VERTEX_SHADER = `#version 300 es
precision highp float;

// Per-vertex: dx, dy, faceId (0 = top, 1 = right, 2 = left)
in vec3 a_cubePos;
in vec4 a_instanceData;

uniform float u_canvasSize;
uniform float u_layoutWidth;
uniform float u_usedHeight;
uniform float u_squareCount;
uniform float u_startTime;
uniform float u_currentTime;
uniform vec2  u_mouse;
uniform float u_flickerIndex;
uniform float u_scale;
uniform float u_enableRepulsion;
uniform float u_enableFlicker;
uniform float u_tileHeightScale; // 0 = flat 2D, 1 = full 3D

out float v_brightness;
out float v_alpha;
out float v_proximityGlow;
out float v_faceBrightness;

const float COS30 = 0.866025;
const float SIN30 = 0.5;

float easeOutBack(float x) {
  float c1 = 1.70158;
  float c3 = c1 + 1.0;
  float t = x - 1.0;
  return 1.0 + c3 * t * t * t + c1 * t * t;
}

void main() {
  float x     = a_instanceData.x;
  float y     = a_instanceData.y;
  float size  = a_instanceData.z;
  float index = a_instanceData.w;

  float dx = a_cubePos.x;
  float dy = a_cubePos.y;
  float faceId = a_cubePos.z;

  // Stage 1 (0-0.5): rotate into the isometric frame. Stage 2 (0.5-1): extrude.
  float transition = clamp(u_tileHeightScale, 0.0, 1.0);
  float rotationProgress;
  float heightProgress;
  if (transition <= 0.0) {
    rotationProgress = 0.0;
    heightProgress = 0.0;
  } else if (transition >= 1.0) {
    rotationProgress = 1.0;
    heightProgress = 1.0;
  } else if (transition < 0.5) {
    rotationProgress = transition * 2.0;
    heightProgress = 0.0;
  } else {
    rotationProgress = 1.0;
    heightProgress = (transition - 0.5) * 2.0;
  }

  float draw = max(u_layoutWidth, u_usedHeight);
  float gridSize = u_canvasSize / draw;
  float offsetY = (u_canvasSize - u_usedHeight * gridSize) * 0.5;

  float baseDuration  = 1400.0;
  float totalStagger  = 1200.0;
  float massFactor    = size * 50.0;
  float staggerDelay  = (index / u_squareCount) * totalStagger + abs(sin(index)) * 100.0;
  float elapsed       = u_currentTime - u_startTime - staggerDelay;
  float duration      = baseDuration + massFactor;

  float overallMs     = u_currentTime - u_startTime;
  float overallProg   = clamp(overallMs / 3000.0, 0.0, 1.0);
  float currentProg   = overallProg >= 1.0 ? 1.0 : clamp(elapsed / duration, 0.0, 1.0);

  if (currentProg <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 0.0, 1.0);
    v_alpha = 0.0;
    v_brightness = 1.0;
    v_proximityGlow = 0.0;
    v_faceBrightness = 1.0;
    return;
  }

  float eased = easeOutBack(currentProg);

  float tx = x;
  float ty = y;
  float shrinkFactor = 1.0;
  v_proximityGlow = 0.0;

  float cappedSize = min(size, 8.0);
  float isoBlockHeight = cappedSize * heightProgress;

  float isoSpanX = (u_layoutWidth + u_usedHeight) * COS30;
  float isoSpanY = (u_layoutWidth + u_usedHeight) * SIN30 + 8.0;
  float isoSpan  = max(isoSpanX, isoSpanY);
  float isoScale = u_canvasSize / (isoSpan * 1.15);

  float centerIsoX = (u_layoutWidth - u_usedHeight) * COS30 * 0.5;
  float centerIsoY = (u_layoutWidth + u_usedHeight) * SIN30 * 0.5 - 4.0;

  if (u_enableRepulsion > 0.5 && overallProg >= 1.0 && u_mouse.x >= 0.0) {
    float mIsoX = (u_mouse.x - u_canvasSize * 0.5) / isoScale + centerIsoX;
    float mIsoY = (u_mouse.y - u_canvasSize * 0.5) / isoScale + centerIsoY;
    float mx = (mIsoX / COS30 + mIsoY / SIN30) * 0.5;
    float my = (mIsoY / SIN30 - mIsoX / COS30) * 0.5;

    float ddx = tx + size * 0.5 - mx;
    float ddy = ty + size * 0.5 - my;
    float dist = sqrt(ddx * ddx + ddy * ddy);
    float radius = 18.0;
    if (dist < radius && dist > 0.001) {
      float sFactor = smoothstep(0.0, radius, dist);
      if (size <= 1.0) {
        v_proximityGlow = 1.0 - sFactor;
      } else {
        float force = (1.0 - dist / radius) * 5.0;
        tx += (ddx / dist) * force;
        ty += (ddy / dist) * force;
        shrinkFactor = sFactor;
        if (size <= 4.0) {
          v_proximityGlow = 1.0 - shrinkFactor;
          shrinkFactor = max(shrinkFactor, 0.5);
        }
      }
    }
  }

  float angle = sin(index * 1234.56) * 6.283185307;
  float distFromCenter = max(u_layoutWidth, u_usedHeight) * (1.2 + cos(index * 789.1) * 0.5);
  float startX = x + cos(angle) * distFromCenter;
  float startY = y + sin(angle) * distFromCenter;

  float curX = startX + (tx - startX) * eased;
  float curY = startY + (ty - startY) * eased;

  float pad = 0.12;
  float innerSize = size - pad * 2.0;
  float effSize   = innerSize * shrinkFactor;
  float effHeight = isoBlockHeight * shrinkFactor;

  float bx = curX + pad + (innerSize - effSize) * 0.5;
  float by = curY + pad + (innerSize - effSize) * 0.5;

  vec3 corner;
  if (faceId < 0.5) {
    corner = vec3(bx + dx * effSize, by + dy * effSize, effHeight);
  } else if (faceId < 1.5) {
    corner = vec3(bx + effSize, by + dx * effSize, dy * effHeight);
  } else {
    corner = vec3(bx + dx * effSize, by + effSize, dy * effHeight);
  }

  float cornerIsoX = (corner.x - corner.y) * COS30;
  float cornerIsoY = (corner.x + corner.y) * SIN30 - corner.z;

  float isoPx = (cornerIsoX - centerIsoX) * isoScale + u_canvasSize * 0.5;
  float isoPy = (cornerIsoY - centerIsoY) * isoScale + u_canvasSize * 0.5;

  // Flat 2D position, for blending during the transition.
  float flatTx = x;
  float flatTy = y;
  float flatShrinkFactor = 1.0;
  float flatProximityGlow = 0.0;

  if (u_enableRepulsion > 0.5 && overallProg >= 1.0 && u_mouse.x >= 0.0) {
    float mFlatX = u_mouse.x / gridSize;
    float mFlatY = (u_mouse.y - offsetY) / gridSize;
    float fddx = flatTx + size * 0.5 - mFlatX;
    float fddy = flatTy + size * 0.5 - mFlatY;
    float fdist = sqrt(fddx * fddx + fddy * fddy);
    float fradius = 18.0;
    if (fdist < fradius && fdist > 0.001) {
      float fsFactor = smoothstep(0.0, fradius, fdist);
      if (size <= 1.0) {
        flatProximityGlow = 1.0 - fsFactor;
      } else {
        float fforce = (1.0 - fdist / fradius) * 5.0;
        flatTx += (fddx / fdist) * fforce;
        flatTy += (fddy / fdist) * fforce;
        flatShrinkFactor = fsFactor;
        if (size <= 4.0) {
          flatProximityGlow = 1.0 - flatShrinkFactor;
          flatShrinkFactor = max(flatShrinkFactor, 0.5);
        }
      }
    }
  }

  if (rotationProgress <= 0.0) {
    v_proximityGlow = flatProximityGlow;
  } else if (rotationProgress < 1.0) {
    v_proximityGlow = mix(flatProximityGlow, v_proximityGlow, rotationProgress);
  }

  float flatCurX = startX + (flatTx - startX) * eased;
  float flatCurY = startY + (flatTy - startY) * eased;

  float flatPad = 0.25;
  float flatInnerSize = size - flatPad * 2.0;
  float flatEffSize = flatInnerSize * flatShrinkFactor;

  float flatBx = flatCurX + flatPad + (flatInnerSize - flatEffSize) * 0.5;
  float flatBy = flatCurY + flatPad + (flatInnerSize - flatEffSize) * 0.5;

  float flatPx = flatBx * gridSize;
  float flatPy = flatBy * gridSize + offsetY;
  float flatPw = flatEffSize * gridSize;

  flatPx = floor(flatPx);
  flatPy = floor(flatPy);
  flatPw = ceil(flatPw);

  float flatVx = flatPx + dx * flatPw;
  float flatVy = flatPy + dy * flatPw;

  float finalPx;
  float finalPy;
  if (rotationProgress <= 0.0) {
    finalPx = flatVx;
    finalPy = flatVy;
  } else if (rotationProgress >= 1.0) {
    finalPx = isoPx;
    finalPy = isoPy;
  } else {
    finalPx = mix(flatVx, isoPx, rotationProgress);
    finalPy = mix(flatVy, isoPy, rotationProgress);
  }

  float cx = u_canvasSize * 0.5;
  float cy = u_canvasSize * 0.5;
  finalPx = cx + (finalPx - cx) * u_scale;
  finalPy = cy + (finalPy - cy) * u_scale;

  float depth;
  if (rotationProgress <= 0.0) {
    depth = 0.0;
  } else {
    float maxRange = u_layoutWidth + u_usedHeight + 8.0 * heightProgress;
    float isoDepth = 1.0 - 2.0 * (corner.x + corner.y + corner.z) / max(maxRange, 1.0);
    depth = rotationProgress >= 1.0 ? isoDepth : isoDepth * rotationProgress;
  }

  gl_Position = vec4((finalPx / u_canvasSize) * 2.0 - 1.0, 1.0 - (finalPy / u_canvasSize) * 2.0, depth, 1.0);

  if (faceId < 0.5) {
    v_faceBrightness = 1.0;
  } else if (faceId < 1.5) {
    v_faceBrightness = 1.0 - rotationProgress * 0.3;
  } else {
    v_faceBrightness = 1.0 - rotationProgress * 0.5;
  }

  float sideFaceAlpha = clamp(heightProgress, 0.0, 1.0);

  bool isFlicker = u_enableFlicker > 0.5 && abs(index - u_flickerIndex) < 0.5;
  float glowBrightness = 1.0 + v_proximityGlow * 1.2;
  v_brightness = isFlicker ? 1.6 : glowBrightness;
  float baseAlpha = isFlicker ? 1.0 : min(1.0, currentProg * 1.5);
  v_alpha = faceId > 0.5 ? baseAlpha * sideFaceAlpha : baseAlpha;
}
`

const ISO_FRAGMENT_SHADER = `#version 300 es
precision highp float;

uniform vec3 u_baseColor;

in float v_brightness;
in float v_alpha;
in float v_proximityGlow;
in float v_faceBrightness;

out vec4 fragColor;

void main() {
  vec3 color = min(vec3(1.0), u_baseColor * v_brightness * v_faceBrightness);
  fragColor = vec4(color, v_alpha);
}
`

// Three quads per block: top (faceId 0), right (1), left (2).
const CUBE_VERTICES = new Float32Array([
	0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 1, 0, 0, 0, 1, 1, 0, 1, 0, 1, 1, 0, 1, 1, 1, 0, 1,
	1, 1, 1, 0, 0, 2, 1, 0, 2, 0, 1, 2, 0, 1, 2, 1, 0, 2, 1, 1, 2
])

const UNIFORM_NAMES = [
	'u_canvasSize',
	'u_layoutWidth',
	'u_usedHeight',
	'u_squareCount',
	'u_startTime',
	'u_currentTime',
	'u_mouse',
	'u_flickerIndex',
	'u_scale',
	'u_baseColor',
	'u_enableRepulsion',
	'u_enableFlicker',
	'u_tileHeightScale'
] as const

type UniformName = (typeof UNIFORM_NAMES)[number]

type GlProgram = {
	program: WebGLProgram
	vao: WebGLVertexArrayObject
	instanceBuffer: WebGLBuffer
	uniforms: Partial<Record<UniformName, WebGLUniformLocation>>
	verticesPerInstance: number
}

type SharedGl = {
	canvas: OffscreenCanvas | HTMLCanvasElement
	gl: WebGL2RenderingContext
	flat: GlProgram
	iso: GlProgram
}

let shared: SharedGl | null = null
let refs = 0
let unavailable = false

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
	const shader = gl.createShader(type)
	if (!shader) throw new Error('createShader failed')
	gl.shaderSource(shader, src.trim())
	gl.compileShader(shader)
	if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
		const log = gl.getShaderInfoLog(shader) ?? '(no info)'
		gl.deleteShader(shader)
		throw new Error(`shader compile error: ${log}`)
	}
	return shader
}

function createSharedGl(size: number): SharedGl {
	const canvas: OffscreenCanvas | HTMLCanvasElement =
		typeof OffscreenCanvas !== 'undefined'
			? new OffscreenCanvas(size, size)
			: document.createElement('canvas')
	canvas.width = size
	canvas.height = size
	const gl = canvas.getContext('webgl2', {
		alpha: false,
		antialias: false,
		premultipliedAlpha: false,
		preserveDrawingBuffer: true
	}) as WebGL2RenderingContext | null
	if (!gl) throw new Error('WebGL2 not available')

	const flat = buildProgram(
		gl,
		VERTEX_SHADER,
		FRAGMENT_SHADER,
		'a_quadPos',
		new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
		2
	)
	const iso = buildProgram(
		gl,
		ISO_VERTEX_SHADER,
		ISO_FRAGMENT_SHADER,
		'a_cubePos',
		CUBE_VERTICES,
		3
	)

	gl.enable(gl.BLEND)
	gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
	for (const p of [flat, iso]) {
		gl.useProgram(p.program)
		if (p.uniforms.u_baseColor) {
			gl.uniform3f(
				p.uniforms.u_baseColor,
				BITMAP_TILE_RGB[0] / 255,
				BITMAP_TILE_RGB[1] / 255,
				BITMAP_TILE_RGB[2] / 255
			)
		}
	}

	return { canvas, gl, flat, iso }
}

function buildProgram(
	gl: WebGL2RenderingContext,
	vsSrc: string,
	fsSrc: string,
	vertexAttr: string,
	vertices: Float32Array,
	components: number
): GlProgram {
	const vs = compile(gl, gl.VERTEX_SHADER, vsSrc)
	const fs = compile(gl, gl.FRAGMENT_SHADER, fsSrc)
	const program = gl.createProgram()
	if (!program) throw new Error('createProgram failed')
	gl.attachShader(program, vs)
	gl.attachShader(program, fs)
	gl.linkProgram(program)
	if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
		const log = gl.getProgramInfoLog(program)
		gl.deleteProgram(program)
		throw new Error(`program link error: ${log}`)
	}
	gl.deleteShader(vs)
	gl.deleteShader(fs)

	const vao = gl.createVertexArray()
	if (!vao) throw new Error('createVertexArray failed')
	gl.bindVertexArray(vao)

	const vertexBuffer = gl.createBuffer()
	gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer)
	gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW)
	const vLoc = gl.getAttribLocation(program, vertexAttr)
	gl.enableVertexAttribArray(vLoc)
	gl.vertexAttribPointer(vLoc, components, gl.FLOAT, false, 0, 0)

	const instanceBuffer = gl.createBuffer()
	if (!instanceBuffer) throw new Error('createBuffer failed')
	gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer)
	gl.bufferData(
		gl.ARRAY_BUFFER,
		MAX_INSTANCES * 4 * Float32Array.BYTES_PER_ELEMENT,
		gl.DYNAMIC_DRAW
	)
	const instLoc = gl.getAttribLocation(program, 'a_instanceData')
	gl.enableVertexAttribArray(instLoc)
	gl.vertexAttribPointer(instLoc, 4, gl.FLOAT, false, 0, 0)
	gl.vertexAttribDivisor(instLoc, 1)
	gl.bindVertexArray(null)

	const uniforms: GlProgram['uniforms'] = {}
	for (const name of UNIFORM_NAMES) {
		const loc = gl.getUniformLocation(program, name)
		if (loc) uniforms[name] = loc
	}
	return {
		program,
		vao,
		instanceBuffer,
		uniforms,
		verticesPerInstance: vertices.length / components
	}
}

let supportProbe: boolean | null = null

/** Whether this browser can create a WebGL2 context (probed once). */
export function webgl2Supported(): boolean {
	if (typeof document === 'undefined') return false
	if (unavailable) return false
	if (supportProbe === null) {
		try {
			supportProbe = !!document.createElement('canvas').getContext('webgl2')
		} catch {
			supportProbe = false
		}
	}
	return supportProbe
}

/** Take a reference on the shared context. Returns null when WebGL2 is unusable. */
export function acquireGl(size: number): SharedGl | null {
	if (unavailable) return null
	try {
		if (!shared) shared = createSharedGl(size)
		refs++
		return shared
	} catch (e) {
		unavailable = true
		console.warn('[bitmap] WebGL2 renderer unavailable, falling back to Canvas 2D', e)
		return null
	}
}

export function releaseGl() {
	refs = Math.max(0, refs - 1)
	if (refs === 0 && shared) {
		const { gl, flat, iso } = shared
		for (const p of [flat, iso]) {
			gl.deleteProgram(p.program)
			gl.deleteVertexArray(p.vao)
			gl.deleteBuffer(p.instanceBuffer)
		}
		shared = null
	}
}

export type GlFrame = {
 lightBackground?: boolean
	size: number
	layout: BitmapLayout
	startTime: number
	now: number
	mouse: { x: number; y: number } | null
	repulsion: boolean
	/** 1 = normal, 0 = imploded to the centre. */
	scale?: number
	/** 0 = flat, 1 = fully extruded isometric blocks; in between animates. */
	tileHeight?: number
}

/** Draw one frame on the shared context and copy it into `target`. */
export function drawGlFrame(s: SharedGl, target: CanvasRenderingContext2D, f: GlFrame) {
	const { gl, canvas } = s
	const n = f.size
	if (canvas.width !== n || canvas.height !== n) {
		canvas.width = n
		canvas.height = n
	}
	const tileHeight = Math.max(0, Math.min(1, f.tileHeight ?? 0))
	const p = tileHeight > 0 ? s.iso : s.flat
	const u = p.uniforms
	const count = Math.min(f.layout.count, MAX_INSTANCES)
	gl.useProgram(p.program)
	gl.bindBuffer(gl.ARRAY_BUFFER, p.instanceBuffer)
	gl.bufferSubData(gl.ARRAY_BUFFER, 0, f.layout.instances.subarray(0, count * 4))
	if (u.u_canvasSize) gl.uniform1f(u.u_canvasSize, n)
	if (u.u_layoutWidth) gl.uniform1f(u.u_layoutWidth, f.layout.layoutWidth)
	if (u.u_usedHeight) gl.uniform1f(u.u_usedHeight, f.layout.usedHeight)
	if (u.u_squareCount) gl.uniform1f(u.u_squareCount, count)
	if (u.u_startTime) gl.uniform1f(u.u_startTime, f.startTime)
	if (u.u_currentTime) gl.uniform1f(u.u_currentTime, f.now)
	if (u.u_flickerIndex) gl.uniform1f(u.u_flickerIndex, -1)
	if (u.u_mouse) gl.uniform2f(u.u_mouse, f.mouse ? f.mouse.x : -1, f.mouse ? f.mouse.y : -1)
	if (u.u_scale) gl.uniform1f(u.u_scale, f.scale ?? 1)
	if (u.u_enableRepulsion) gl.uniform1f(u.u_enableRepulsion, f.repulsion ? 1 : 0)
	if (u.u_enableFlicker) gl.uniform1f(u.u_enableFlicker, 0)
	if (u.u_tileHeightScale) gl.uniform1f(u.u_tileHeightScale, tileHeight)
	if (tileHeight > 0) {
		gl.enable(gl.DEPTH_TEST)
		gl.depthFunc(gl.LESS)
	} else {
		gl.disable(gl.DEPTH_TEST)
	}
	gl.viewport(0, 0, n, n)
	gl.clearColor(f.lightBackground ? 1 : BITMAP_BG[0], f.lightBackground ? 1 : BITMAP_BG[1], f.lightBackground ? 1 : BITMAP_BG[2], 1)
	gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
	gl.bindVertexArray(p.vao)
	gl.drawArraysInstanced(gl.TRIANGLES, 0, p.verticesPerInstance, count)
	gl.bindVertexArray(null)
	target.clearRect(0, 0, n, n)
	target.drawImage(canvas as CanvasImageSource, 0, 0)
}
